/**
 * http.ts
 * Shared fetch machinery for the RIGX Operador PWA — extracted out of
 * `api.ts` (locx-voz-multi-intencion, Phase 9) so a second per-feature client
 * file (`voice-intake.ts`) can reuse the exact same `request<T>`/`ApiError`/
 * `API_BASE` posture instead of a second, drifting copy-paste. `api.ts`
 * re-exports `ApiError` unchanged so every existing `from '../lib/api.js'`
 * import keeps working with zero changes.
 *
 * Auth is the httpOnly `suite_session` cookie — `credentials:'include'`
 * makes the browser send it (and store any `Set-Cookie`) on cross-origin
 * app↔api calls. No token is ever held in JS.
 */
import type { ErrorEnvelope } from '@suite/contracts-kernel';

export const API_BASE = (import.meta.env.VITE_API_URL ?? 'http://localhost:3000').replace(/\/+$/, '');

/** Thrown on any non-2xx response. Carries the parsed error envelope when present. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  /** The envelope's per-field errors, when the server sent them. */
  readonly fieldErrors: readonly { field: string; message: string }[];
  constructor(status: number, code: string, message: string, fieldErrors: readonly { field: string; message: string }[] = []) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }
}

// ---------------------------------------------------------------------------
// Lost session (fix/operator-session-lost): a 401 on any API call outside
// /auth/* means the session cookie is gone — expired, or cleared by signing
// out in the web panel, which shares this API and its cookie. Reported to one
// handler (session-lost.ts, wired in main.ts) that sends the user to the
// login instead of leaving every screen failing. /auth/* is left to the login
// and session code, which handle their own 401s.
// ---------------------------------------------------------------------------

let unauthorizedHandler: (() => void) | null = null;

export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** Call with the status of any API response, including raw fetch() calls. */
export function noticeStatus(status: number, path: string): void {
  if (status === 401 && !path.startsWith('/auth/')) unauthorizedHandler?.();
}

/**
 * No connection, or a passing server problem (timeout, rate limit, 5xx — a
 * weak signal or a deploy): worth falling back to what the phone kept.
 */
export function isUnreachable(err: unknown): boolean {
  if (!(err instanceof ApiError)) return false;
  return err.status === 0 || err.status === 408 || err.status === 429 || err.status >= 500;
}

/**
 * GET /files/:id as a Blob. Raw fetch, not request<T>(): the body is bytes,
 * and the route is tenant-scoped by the session cookie, so a plain cross-
 * origin <img>/<audio> src would go without credentials and 401/404.
 */
export async function fetchFileBlob(fileId: string, failMessage: string): Promise<Blob> {
  const path = `/files/${fileId}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, { credentials: 'include' });
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }
  if (!res.ok) {
    noticeStatus(res.status, path);
    throw new ApiError(res.status, 'NOT_FOUND', failMessage);
  }
  return res.blob();
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Explicit Bearer token for the one call that needs it (select-company). */
  bearer?: string;
}

export async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (opts.bearer) headers['Authorization'] = `Bearer ${opts.bearer}`;

  const init: RequestInit = { method: opts.method ?? 'GET', headers, credentials: 'include' };
  if (opts.body !== undefined) init.body = JSON.stringify(opts.body);

  let res: Response;
  try {
    res = await fetch(`${API_BASE}${path}`, init);
  } catch {
    throw new ApiError(0, 'NETWORK_ERROR', 'No se pudo conectar con el servidor.');
  }

  if (!res.ok) {
    noticeStatus(res.status, path);
    let code = 'UNKNOWN';
    let message = `Request failed with status ${res.status}`;
    let fieldErrors: { field: string; message: string }[] = [];
    try {
      const envelope = (await res.json()) as Partial<ErrorEnvelope>;
      if (typeof envelope.code === 'string') code = envelope.code;
      if (typeof envelope.message === 'string') message = envelope.message;
      if (Array.isArray(envelope.errors)) fieldErrors = envelope.errors;
    } catch {
      // Body was not JSON — keep the generic message.
    }
    throw new ApiError(res.status, code, message, fieldErrors);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}
